import { z } from 'zod';

/**
 * SupportHours interface
 * Represents the hours of operation for the support line for a particular day.
 */
const DayOfWeek = {
    MONDAY: 'monday',
    TUESDAY: 'tuesday',
    WEDNESDAY: 'wednesday',
    THURSDAY: 'thursday',
    FRIDAY: 'friday',
    SATURDAY: 'saturday',
    SUNDAY: 'sunday',
};
const dayOfWeekValues = [
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
    DayOfWeek.SUNDAY,
];
const dayOfWeekSchema = z.enum(dayOfWeekValues);
//--------------------------------
// Zod Schemas
//--------------------------------
// `HH:MM` 24-hour wire format. Hour `00`–`23`, minute `00`–`59`. No
// seconds, no timezone suffix — schedules are stored alongside
// `support.timezone` and have no meaningful sub-minute precision.
//
// Historical context (ENGNR-14241): the Admin Portal `formatTimeString`
// writer used to emit `HH:MM:SS`, which slipped past the previous
// `z.string()` shape and silently broke the strict `HH:MM` runtime parser
// in `business-hours.ts`. The regex below now blocks new dirty data; the
// parser was loosened in tandem so that any pre-tightening rows still
// parse until the one-time migration runs.
const HHMM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
const HHMM_ERROR = 'must be HH:MM in 24-hour format (e.g. "09:00", "18:30")';
const supportHoursSchema = z.object({
    day: dayOfWeekSchema,
    open: z.string().regex(HHMM_REGEX, { message: `open ${HHMM_ERROR}` }),
    close: z.string().regex(HHMM_REGEX, { message: `close ${HHMM_ERROR}` }),
});
const supportScheduleSchema = z.record(dayOfWeekSchema, supportHoursSchema);
function isBlankStoredTime(value) {
    return value === undefined || value === null || value === '';
}
function isClosedStoredDay(value) {
    if (value === null)
        return true;
    if (typeof value !== 'object' || Array.isArray(value))
        return false;
    const record = value;
    return isBlankStoredTime(record.open) && isBlankStoredTime(record.close);
}
/**
 * Legacy schedules store a closed day as null or `{ open: '', close: '' }`.
 * Drop those days so a read treats the day as closed. A day that is open
 * still has to carry HH:MM times. The write DTO stays strict.
 */
function omitClosedStoredScheduleDays(schedule) {
    if (schedule === null || schedule === undefined)
        return schedule;
    if (typeof schedule !== 'object' || Array.isArray(schedule))
        return schedule;
    const openDays = {};
    for (const [day, hours] of Object.entries(schedule)) {
        if (isClosedStoredDay(hours))
            continue;
        openDays[day] = hours;
    }
    return openDays;
}

export { DayOfWeek, dayOfWeekSchema, dayOfWeekValues, omitClosedStoredScheduleDays, supportHoursSchema, supportScheduleSchema };
//# sourceMappingURL=SupportHours.js.map
